import React, { useEffect, Dispatch, SetStateAction, useState } from 'react';
import { BridgeError, BridgeErrorType } from '../../../../utils';
import FileInputWithModal from './file-input-with-modal';
import * as api from '../../../../api';
import { useLocation } from 'wouter';
import { useModal } from './modals/modal';
import DeleteKeyBarWithButton from './delete-key-bar-with-button';
import Spinner from '../../../spinner';

interface KeyUploaderProps {
  setError: Dispatch<SetStateAction<{ message: string; detail: string }>>;
}

const KeyUploader = ({ setError }: KeyUploaderProps) => {
  const [modalRender, popup] = useModal();
  const [, setLocation] = useLocation();
  const [keyJson, setKeyJson] = useState<string | null>(null);
  const [hadKeyAlready, setHadKeyAlready] = useState(false);
  const [loading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await api.getWorkspaceKey();
        setHadKeyAlready(true);
      } catch (_err) {
        const err = _err as BridgeError;

        handleError(err, 'get service account key');
      }
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (keyJson) {
        try {
          // fail if key is not valid json
          const parsedKey = JSON.parse(keyJson);
          await api.uploadWorkspaceKey(parsedKey);
          await api.restartServer();
          setLocation('/app/login?success=key-uploaded');
        } catch (_err) {
          const err = _err as BridgeError;
          handleError(err, 'upload service account key');
        }
      }
    })();
  }, [keyJson]);

  const onDeleteClick = async () => {
    const cont = await popup({
      titleText: 'Delete Service Account Key',
      subtitleText: '',
      confirmText: 'Confirm and Delete',
      cancelText: 'Cancel',
      bodyText:
        'You will need to enter your bearer token after deleting your Google Workspace service account key.',
      isCritical: true,
    });

    if (cont) {
      await api.deleteWorkspaceKey();
      await api.restartServer();
      setLocation('/app/login?success=key-deleted');
    }
  };

  const handleError = (err: BridgeError, operationSummary: string) => {
    switch (err.type) {
      case BridgeErrorType.Unauthorized:
        setError({
          message: err.message,
          detail: 'Redirecting to login page in 2 seconds',
        });
        setTimeout(() => setLocation('/app/login'), 2000);

      // not found just means that our key file was not present,
      // so we can ignore this error.
      case BridgeErrorType.NotFound:
        break;

      default:
        setError({
          message: 'Unable to ' + operationSummary + '.',
          detail: err.message,
        });
    }
  };

  return (
    <>
      {modalRender}
      <div className="flex flex-col">
        <h2 className="block text-md mb-2 font-bold text-gray-900">
          Workspace Key
        </h2>

        {loading ? (
          <div className="flex justify-center w-full mt-1 mb-6">
            <Spinner />
          </div>
        ) : hadKeyAlready ? (
          <div className="mb-4">
            <DeleteKeyBarWithButton onDeleteClick={onDeleteClick} />
          </div>
        ) : (
          <div className="flex mb-4">
            <FileInputWithModal
              buttonText="Upload Service Account Key File"
              modalConfig={{
                titleText: 'Upload Key File',
                subtitleText: '',
                confirmText: 'Confirm and Upload',
                cancelText: 'Cancel',
                bodyText:
                  'You will need to enter your bearer token after uploading your Google Workspace key file.',
                isCritical: false,
              }}
              setFileText={setKeyJson}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default KeyUploader;
